/**
 * PracticeScene.ts — The main gameplay scene
 *
 * Renders the Cartesian grid, soldiers, and trajectories.
 * Manages the turn-based flow: player input → trajectory animation →
 * collision check → bot turn → repeat.
 */

import * as Phaser from 'phaser';
import { HudUI } from '../ui/HudUI';
import { parseFunction } from '../core/parser';
import { computeTrajectory, DEFAULT_FIELD_BOUNDS, type TrajectoryPoint, createCoordMapper, type CoordMapper as GlobalCoordMapper } from '../core/trajectory';
import {
  createMatch,
  getCurrentSoldier,
  applyTurn,
  getAliveCount,
  type MatchState,
  type TurnResult,
  DEFAULT_MATCH_CONFIG,
} from '../core/match';
import { generateBotFunction, BotDifficulty } from '../core/bots';
import { ReplayTurn, ReplayChatMessage, ReplayData, saveLocalReplay, exportReplayCode } from '../core/replay';
import { playShootSound, playExplosionSound, playDeathSound, playVictorySound, playDefeatSound, playTickSound } from '../core/audio';

/** Maps game-world coordinates to screen pixels */
interface CoordMapper {
  toScreenX: (worldX: number) => number;
  toScreenY: (worldY: number) => number;
  toWorldX: (screenX: number) => number;
  toWorldY: (screenY: number) => number;
}

interface SavedTrajectory {
  teamIndex: number;
  points: TrajectoryPoint[];
}

export class PracticeScene extends Phaser.Scene {
  private hudUI: HudUI;
  private matchState!: MatchState;
  private difficulty: BotDifficulty = BotDifficulty.Medium;
  private mode: { 
    team1Count: number; 
    team2Count: number; 
    keepTraces: boolean; 
    obstacleDensity: import('../core/obstacles').ObstacleDensity;
    showCoordinates: boolean;
    turnTimer: number;
    chatEquations: boolean;
  } = {
    team1Count: 2,
    team2Count: 2,
    keepTraces: true,
    obstacleDensity: 'none',
    showCoordinates: false,
    turnTimer: 60,
    chatEquations: true,
  };

  private dynamicBounds = DEFAULT_FIELD_BOUNDS;

  private turnTimerEvent: Phaser.Time.TimerEvent | null = null;
  private secondsRemaining: number = 0;

  // Graphics layers
  private gridGraphics!: Phaser.GameObjects.Graphics;
  private obstacleGraphics!: Phaser.GameObjects.Graphics;
  private trajectoryGraphics!: Phaser.GameObjects.Graphics;
  private soldierGroup!: Phaser.GameObjects.Group;
  private explosionGraphics!: Phaser.GameObjects.Graphics;

  // Coordinate mapping
  private mapper!: CoordMapper;

  // Animation state
  private isAnimating: boolean = false;
  private currentTrajectory: TrajectoryPoint[] = [];
  private animationIndex: number = 0;
  private animationTimer: number = 0;

  // Team colors — matching the light minimalist CSS palette
  private readonly TEAM_COLORS = [0x1D4E89, 0xF76707]; // team1, team2 from spec
  private readonly TRAJECTORY_COLORS = [0x1D4E89, 0xd94040]; // same as team

  // Soldier label text objects (for coordinate labels)
  private soldierLabels: Phaser.GameObjects.Text[] = [];
  
  // Grid axis label text objects
  private gridLabels: Phaser.GameObjects.Text[] = [];

  // Saved trajectories (max 5 per team)
  private pastTrajectories: SavedTrajectory[] = [];
  private tracesEnabled: boolean = true;

  // Replay recording state
  private replayTurns: ReplayTurn[] = [];
  private replayChatLog: ReplayChatMessage[] = [];
  private initialSoldiers: Array<{ teamIndex: number; x: number; y: number }> = [];

  // Track turn info for history logging
  private lastTurnExpression: string | null = null;
  private lastShooter: { teamIndex: number; isBot: boolean } | null = null;
  private lastKillingExpression: string | null = null;
  private soldierNames: Map<string, string> = new Map();

  private readonly BOT_GAMERTAGS = [
    'xX_Sniper_Xx', 'NoScope420', 'MathGod99', 'EulerHaxor', 
    'Pythagoras_TTV', 'CalculusDemon', 'SirPlotALot', 'TangentTroll',
    'Derivativ3', 'L33t_Int3gral', 'GraphDestroyer', 'SineWaveSurfer',
    'Limitless_Pro', 'VectorVictor', 'MatrixNeo'
  ];

  constructor() {
    super({ key: 'PracticeScene' });
    this.hudUI = new HudUI();
  }

  init(data: { difficulty?: BotDifficulty; mode?: { team1Count: number; team2Count: number; keepTraces: boolean; obstacleDensity?: any; showCoordinates?: boolean; turnTimer?: number; chatEquations?: boolean } }): void {
    this.difficulty = data.difficulty ?? BotDifficulty.Medium;
    this.lastKillingExpression = null;
    this.replayTurns = [];
    this.replayChatLog = [];
    this.initialSoldiers = [];
    if (data.mode) {
      this.mode = {
        ...data.mode,
        obstacleDensity: data.mode.obstacleDensity || 'none',
        showCoordinates: data.mode.showCoordinates ?? false,
        turnTimer: data.mode.turnTimer ?? 60,
        chatEquations: data.mode.chatEquations ?? true,
      };
    }
    this.tracesEnabled = this.mode.keepTraces;
  }

  create(): void {
    const { width, height } = this.scale;

    // Use unified coordinate mapper which handles 1:1 aspect ratio bounds automatically
    this.mapper = createCoordMapper(width, height) as any;
    this.dynamicBounds = (this.mapper as any).bounds || DEFAULT_FIELD_BOUNDS;

    // Create graphics layers (ordered back to front: grid -> obstacles -> trajectory -> soldiers -> explosions)
    this.gridGraphics = this.add.graphics();
    this.obstacleGraphics = this.add.graphics();
    this.trajectoryGraphics = this.add.graphics();
    this.soldierGroup = this.add.group();
    this.explosionGraphics = this.add.graphics();

    // Reset past trajectories
    this.pastTrajectories = [];

    // Draw the grid
    this.drawGrid();

    // Create the match
    const matchConfig = {
      ...DEFAULT_MATCH_CONFIG,
      team1Count: this.mode.team1Count,
      team2Count: this.mode.team2Count,
      obstacleDensity: this.mode.obstacleDensity,
      bounds: this.dynamicBounds, // Use exact screen aspect ratio bounds
    };
    this.matchState = createMatch(matchConfig);

    // Draw initial obstacles
    this.drawObstacles();

    // Save initial soldier positions for replay export
    this.initialSoldiers = this.matchState.teams.flatMap((t, tIdx) =>
      t.soldiers.map(s => ({ teamIndex: tIdx, x: s.x, y: s.y }))
    );

    // Draw soldiers
    this.drawSoldiers();

    // Show HUD
    this.setupHud();

    // Start game
    this.updateHudState();
    this.assignSoldierNames();
    this.drawSoldiers();
  }

  private assignSoldierNames(): void {
    this.soldierNames.clear();
    const availableTags = [...this.BOT_GAMERTAGS].sort(() => Math.random() - 0.5);
    let botIndex = 0;

    for (let tIdx = 0; tIdx < this.matchState.teams.length; tIdx++) {
      const team = this.matchState.teams[tIdx];
      const isPlayerTeam = tIdx === 0;
      
      for (let sIdx = 0; sIdx < team.soldiers.length; sIdx++) {
        const key = `${tIdx}-${sIdx}`;
        if (!isPlayerTeam || sIdx > 0) {
          // Bots and player teammates get random gamertags
          const tag = availableTags[botIndex % availableTags.length];
          this.soldierNames.set(key, `${tag} [BOT]`);
          botIndex++;
        } else {
          // The main player character
          this.soldierNames.set(key, 'YOU');
        }
      }
    }
  }

  private setupHud(): void {
    // Wait for the font to load before creating labels
    // We assume IBM Plex Mono is loaded by the time practice starts, but we use it anyway
    this.events.on('shutdown', () => {});
    
    this.hudUI.show({
      onSubmitFunction: (expr: string, force?: boolean) => this.handlePlayerTurn(expr, force),
      onNewGame: () => {
        this.restartGame();
      },
      onChatSend: (msg: string) => {
        const playerName = localStorage.getItem('mathillery_player_name') || 'YOU';
        this.hudUI.addChatEntry(playerName.toUpperCase(), msg, true);
        this.replayChatLog.push({
          turnIndex: this.replayTurns.length,
          senderName: playerName.toUpperCase(),
          text: msg,
          isPlayer: true
        });
        
        // Show bubble over human player soldier
        const humanSoldier = this.matchState.teams[0].soldiers.find(s => !s.isBot);
        if (humanSoldier && humanSoldier.alive) {
          this.showChatBubble(humanSoldier.id, msg);
        }
        
        // Bot responds to chat
        this.time.delayedCall(1000, () => {
           const responses = ["I'm trying to calculate here.", "Less chatting, more graphing.", "f(x) = RIP", "Is that a polynomial?"];
           const botMsg = responses[Math.floor(Math.random() * responses.length)];
           
           const aliveBots = this.matchState.teams[1].soldiers.filter(s => s.alive);
           if (aliveBots.length > 0) {
             const randomBot = aliveBots[Math.floor(Math.random() * aliveBots.length)];
             const sIdx = this.matchState.teams[1].soldiers.indexOf(randomBot);
             let botName = this.soldierNames.get(`1-${sIdx}`) || 'BOT';
             if (botName.includes('BOT')) {
               botName = botName.replace(' [BOT]', '');
             }
             
             this.hudUI.addChatEntry(botName.toUpperCase(), botMsg, false);
             this.replayChatLog.push({
               turnIndex: this.replayTurns.length,
               senderName: botName.toUpperCase(),
               text: botMsg,
               isPlayer: false
             });
             this.showChatBubble(randomBot.id, botMsg);
           }
        });
      },
      onBackToMenu: () => {
        this.hudUI.destroy();
        this.scene.start('MenuScene');
      },
      onCopyReplay: () => {
        const replayData: ReplayData = {
          version: 1,
          createdAt: Date.now(),
          difficulty: this.difficulty,
          team1Count: this.mode.team1Count,
          team2Count: this.mode.team2Count,
          keepTraces: this.mode.keepTraces,
          obstacleDensity: this.mode.obstacleDensity,
          initialSoldiers: this.initialSoldiers,
          initialObstacles: this.matchState.obstacles,
          turns: this.replayTurns,
        };
        const code = exportReplayCode(replayData);
        if (navigator.clipboard) {
          navigator.clipboard.writeText(code);
          this.hudUI.showToast('Replay code copied to clipboard!');
        } else {
          this.hudUI.showToast('Replay Code copied!');
        }
      },
    });
  }

  private showChatBubble(soldierId: string, message: string): void {
    const soldier = this.matchState.teams.flatMap(t => t.soldiers).find(s => s.id === soldierId);
    if (!soldier) return;

    // Check if there's an existing bubble and destroy it
    const existingBubble = this.children.getByName(`chat-bubble-${soldierId}`);
    if (existingBubble) {
      existingBubble.destroy();
    }

    const sx = this.mapper.toScreenX(soldier.x);
    const sy = this.mapper.toScreenY(soldier.y);

    const bubbleGroup = this.add.container(sx, sy - 45);
    bubbleGroup.setName(`chat-bubble-${soldierId}`);
    
    const text = this.add.text(0, -8, message, {
      fontFamily: '"Space Grotesk", sans-serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#1e293b',
      align: 'center',
    }).setOrigin(0.5);

    const paddingX = 14;
    const paddingY = 8;
    const width = text.width + paddingX * 2;
    const height = text.height + paddingY * 2;
    const radius = 12;

    const graphics = this.add.graphics();
    
    const w = width;
    const h = height;
    const cx = -w/2;
    const cy = -h/2 - 8;

    const drawBubblePath = (g: Phaser.GameObjects.Graphics) => {
      g.beginPath();
      g.moveTo(cx + radius, cy);
      g.lineTo(cx + w - radius, cy);
      g.arc(cx + w - radius, cy + radius, radius, -Math.PI/2, 0);
      g.lineTo(cx + w, cy + h - radius);
      g.arc(cx + w - radius, cy + h - radius, radius, 0, Math.PI/2);
      
      // Tail
      g.lineTo(8, cy + h);
      g.lineTo(0, cy + h + 8);
      g.lineTo(-8, cy + h);
      
      g.lineTo(cx + radius, cy + h);
      g.arc(cx + radius, cy + h - radius, radius, Math.PI/2, Math.PI);
      g.lineTo(cx, cy + radius);
      g.arc(cx + radius, cy + radius, radius, Math.PI, -Math.PI/2);
      g.closePath();
    };

    // Shadow
    graphics.fillStyle(0x000000, 0.15);
    graphics.translateCanvas(0, 4);
    drawBubblePath(graphics);
    graphics.fillPath();
    graphics.translateCanvas(0, -4);

    // Bubble Body
    graphics.fillStyle(0xffffff, 1);
    drawBubblePath(graphics);
    graphics.fillPath();
    
    // Bubble Stroke (Primary Blue)
    graphics.lineStyle(2, 0x1D4E89, 1);
    graphics.strokePath();

    bubbleGroup.add(graphics);
    bubbleGroup.add(text);
    
    bubbleGroup.setScale(1);
    bubbleGroup.setAlpha(1);

    this.time.delayedCall(3500, () => {
      if (bubbleGroup) bubbleGroup.destroy();
    });
  }



  private drawGridLines(g: Phaser.GameObjects.Graphics): void {
    const m = this.mapper;
    const bounds = this.dynamicBounds;

    // Grid colors from spec
    const majorColor = 0xd8d5ce;
    const minorColor = 0xeae8e3;
    const axisColor = 0xb4b2a9;

    // Major grid lines
    g.lineStyle(1, majorColor, 1);
    g.beginPath();
    const startXMajor = Math.ceil(bounds.minX / 5) * 5;
    for (let x = startXMajor; x <= bounds.maxX; x += 5) {
      const sx = m.toScreenX(x);
      g.moveTo(sx, m.toScreenY(bounds.maxY));
      g.lineTo(sx, m.toScreenY(bounds.minY));
    }
    const startYMajor = Math.ceil(bounds.minY / 5) * 5;
    for (let y = startYMajor; y <= bounds.maxY; y += 5) {
      const sy = m.toScreenY(y);
      g.moveTo(m.toScreenX(bounds.minX), sy);
      g.lineTo(m.toScreenX(bounds.maxX), sy);
    }
    g.strokePath();

    // Minor grid
    g.lineStyle(1, minorColor, 1);
    g.beginPath();
    const startXMinor = Math.ceil(bounds.minX);
    for (let x = startXMinor; x <= bounds.maxX; x += 1) {
      if (x % 5 === 0) continue;
      const sx = m.toScreenX(x);
      g.moveTo(sx, m.toScreenY(bounds.maxY));
      g.lineTo(sx, m.toScreenY(bounds.minY));
    }
    const startYMinor = Math.ceil(bounds.minY);
    for (let y = startYMinor; y <= bounds.maxY; y += 1) {
      if (y % 5 === 0) continue;
      const sy = m.toScreenY(y);
      g.moveTo(m.toScreenX(bounds.minX), sy);
      g.lineTo(m.toScreenX(bounds.maxX), sy);
    }
    g.strokePath();

    // Axes
    g.lineStyle(2, axisColor, 1);
    g.beginPath();
    // X-axis
    const yZero = m.toScreenY(0);
    g.moveTo(m.toScreenX(bounds.minX), yZero);
    g.lineTo(m.toScreenX(bounds.maxX), yZero);
    // Y-axis
    const xZero = m.toScreenX(0);
    g.moveTo(xZero, m.toScreenY(bounds.maxY));
    g.lineTo(xZero, m.toScreenY(bounds.minY));
    g.strokePath();
  }

  private drawGrid(): void {
    const g = this.gridGraphics;
    g.clear();
    
    this.drawGridLines(g);

    const m = this.mapper;
    const bounds = this.dynamicBounds;
    const yZero = m.toScreenY(0);
    const xZero = m.toScreenX(0);
    const startXMajor = Math.ceil(bounds.minX / 5) * 5;
    const startYMajor = Math.ceil(bounds.minY / 5) * 5;

    // Axis labels
    // Clear old labels to prevent memory leak on redraws
    for (const label of this.gridLabels) {
      label.destroy();
    }
    this.gridLabels = [];

    const textColor = '#8A8780'; // var(--color-text-muted)

    // Draw the 0 at the origin once
    const originLabel = this.add.text(xZero - 8, yZero + 6, '0', {
      fontFamily: 'IBM Plex Mono, monospace',
      fontSize: '10px',
      color: textColor,
    }).setOrigin(1, 0);
    this.gridLabels.push(originLabel);

    for (let x = startXMajor; x <= bounds.maxX; x += 5) {
      if (x === 0) continue;
      const lbl = this.add.text(m.toScreenX(x), yZero + 6, String(x), {
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: '10px',
        color: textColor,
      }).setOrigin(0.5, 0);
      this.gridLabels.push(lbl);
    }
    for (let y = startYMajor; y <= bounds.maxY; y += 5) {
      if (y === 0) continue;
      const lbl = this.add.text(xZero - 8, m.toScreenY(y), String(y), {
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: '10px',
        color: textColor,
      }).setOrigin(1, 0.5);
      this.gridLabels.push(lbl);
    }
  }

  /**
   * Draws circular obstacles with carved bite holes.
   */
  private drawObstacles(): void {
    if (!this.obstacleGraphics || !this.matchState || !this.matchState.obstacles) return;
    const g = this.obstacleGraphics;
    g.clear();

    const m = this.mapper;
    const bounds = this.dynamicBounds;

    const majorColor = 0xd8d5ce;
    const minorColor = 0xeae8e3;
    const axisColor = 0xb4b2a9;

    for (const obs of this.matchState.obstacles) {
      const sx = m.toScreenX(obs.x);
      const sy = m.toScreenY(obs.y);
      const sRadius = Math.abs(m.toScreenX(obs.x + obs.radius) - sx);

      const obsColor = 0x2B2A28; 
      const obsStroke = 0x2B2A28;

      g.fillStyle(obsColor, 1.0);
      g.fillCircle(sx, sy, sRadius);
      g.lineStyle(2, obsStroke, 1.0);
      g.strokeCircle(sx, sy, sRadius);

      const bgColor = 0xF8F7F4;

      for (const bite of obs.bites) {
        const bsx = m.toScreenX(bite.x);
        const bsy = m.toScreenY(bite.y);
        const bsRadius = Math.abs(m.toScreenX(bite.x + bite.radius) - bsx);
        
        g.fillStyle(bgColor, 1.0);
        g.fillCircle(bsx, bsy, bsRadius);

        // Mathematical Grid Clipping
        // Draw the exact grid line segments that fall within this bite circle
        const drawVertLine = (x: number, color: number, width: number) => {
            if (Math.abs(x - bite.x) < bite.radius) {
                const dy = Math.sqrt(bite.radius * bite.radius - (x - bite.x) ** 2);
                const sy1 = m.toScreenY(bite.y - dy);
                const sy2 = m.toScreenY(bite.y + dy);
                const px = m.toScreenX(x);
                g.lineStyle(width, color, 1);
                g.beginPath();
                g.moveTo(px, sy1);
                g.lineTo(px, sy2);
                g.strokePath();
            }
        };

        const drawHorizLine = (y: number, color: number, width: number) => {
            if (Math.abs(y - bite.y) < bite.radius) {
                const dx = Math.sqrt(bite.radius * bite.radius - (y - bite.y) ** 2);
                const sx1 = m.toScreenX(bite.x - dx);
                const sx2 = m.toScreenX(bite.x + dx);
                const py = m.toScreenY(y);
                g.lineStyle(width, color, 1);
                g.beginPath();
                g.moveTo(sx1, py);
                g.lineTo(sx2, py);
                g.strokePath();
            }
        };

        const startX = Math.ceil(bite.x - bite.radius);
        const endX = Math.floor(bite.x + bite.radius);
        for (let x = startX; x <= endX; x++) {
            if (x === 0) {
                drawVertLine(x, axisColor, 2);
            } else if (x % 5 === 0) {
                drawVertLine(x, majorColor, 1);
            } else {
                drawVertLine(x, minorColor, 1);
            }
        }

        const startY = Math.ceil(bite.y - bite.radius);
        const endY = Math.floor(bite.y + bite.radius);
        for (let y = startY; y <= endY; y++) {
            if (y === 0) {
                drawHorizLine(y, axisColor, 2);
            } else if (y % 5 === 0) {
                drawHorizLine(y, majorColor, 1);
            } else {
                drawHorizLine(y, minorColor, 1);
            }
        }
      }
    }
  }

  /**
   * Draws all soldiers on the grid.
   */
  private drawSoldiers(): void {
    this.soldierGroup.clear(true, true);
    const m = this.mapper;
    const currentSoldier = getCurrentSoldier(this.matchState);
    
    for (const [colorIndex, team] of this.matchState.teams.entries()) {
      const teamColorStr = colorIndex === 0 ? '#1D4E89' : '#d94040';
      const teamColorHex = colorIndex === 0 ? 0x1D4E89 : 0xd94040;

      for (const soldier of team.soldiers) {
        const screenX = m.toScreenX(soldier.x);
        const screenY = m.toScreenY(soldier.y);

        if (!soldier.alive) {
          const xText = this.add.text(screenX, screenY, 'X', {
            fontFamily: '"Space Grotesk", sans-serif',
            fontSize: '20px',
            color: teamColorStr,
            fontStyle: 'bold',
            stroke: '#ffffff',
            strokeThickness: 3,
          }).setOrigin(0.5);
          this.soldierGroup.add(xText);
          continue;
        }

        const isCurrent = soldier.id === currentSoldier.id;

        // Base container for the soldier (to easily add bubbles later)
        const container = this.add.container(screenX, screenY);
        container.setName(`soldier-container-${soldier.id}`);
        this.soldierGroup.add(container);

        const hitRadius = this.matchState.config.hitRadius || 0.5;
        const SOLDIER_RADIUS = m.toScreenX(hitRadius) - m.toScreenX(0);

        const circle = this.add.circle(0, 0, SOLDIER_RADIUS, teamColorHex);
        circle.setStrokeStyle(2, 0xffffff, 1.0);
        container.add(circle);

        // Name label container
        const textGroup = this.add.container(0, -SOLDIER_RADIUS - 18);
        
        const sIdx = team.soldiers.indexOf(soldier);
        const key = `${colorIndex}-${sIdx}`;
        let nameToDisplay = this.soldierNames.get(key) || '';
        if (nameToDisplay.includes('BOT')) {
          nameToDisplay = nameToDisplay.replace(' [BOT]', '');
        }

        const nameLabel = this.add.text(0, 0, nameToDisplay, {
          fontFamily: '"Space Grotesk", sans-serif',
          fontSize: '11px',
          color: teamColorStr,
          fontStyle: 'bold',
          stroke: '#ffffff',
          strokeThickness: 3,
        }).setOrigin(0.5);

        textGroup.add(nameLabel);

        if (isCurrent) {
          const arrow = this.add.text(0, 10, '▼', {
            fontFamily: 'Outfit',
            fontSize: '10px',
            color: teamColorStr,
            stroke: '#ffffff',
            strokeThickness: 2,
          }).setOrigin(0.5);
          
          this.tweens.add({
            targets: arrow,
            y: 13,
            duration: 600,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
          });
          textGroup.add(arrow);
        }

        container.add(textGroup);
      }
    }
  }

  /**
   * Handles the player submitting a function.
   */
  private handlePlayerTurn(expression: string, force: boolean = false): void {
    if (this.isAnimating || this.matchState.finished) return;

    const currentSoldier = getCurrentSoldier(this.matchState);
    if (currentSoldier.isBot) return; // Not the player's turn

    // Parse the function
    let result = parseFunction(expression);
    if (!result.ok && force) { // force turn timeout
      result = parseFunction('0');
    } else if (!result.ok) {
      this.hudUI.showError(result.message);
      return;
    }

    if (!result.ok) return;

    this.hudUI.hideError();
    this.hudUI.setInputEnabled(false);
    this.stopTurnTimer();

    this.lastTurnExpression = result.expression;
    this.lastShooter = { teamIndex: currentSoldier.teamIndex, isBot: false };

    // Apply the turn
    const turnResult = applyTurn(this.matchState, result.evaluate);

    // Animate the trajectory
    this.animateTrajectory(
      turnResult.trajectory,
      this.matchState.teams[currentSoldier.teamIndex] === this.matchState.teams[0] ? 0 : 1,
      turnResult,
      result.expression
    );
  }



  /**
   * Redraws all saved trajectories (max 5 per team) with fading opacities.
   */
  private redrawPastTrajectories(): void {
    const g = this.trajectoryGraphics;
    g.clear();

    if (!this.tracesEnabled) return;

    const m = this.mapper;
    for (let teamIndex = 0; teamIndex < 2; teamIndex++) {
      const trajs = this.pastTrajectories.filter(t => t.teamIndex === teamIndex);
      const color = this.TRAJECTORY_COLORS[teamIndex];
      const count = trajs.length;

      trajs.forEach((traj, idx) => {
        // Fading opacity: oldest 0.15, newest 0.70
        const alpha = count === 1 ? 0.70 : 0.15 + (idx / (count - 1)) * 0.55;

        g.lineStyle(1.5, color, alpha);
        g.beginPath();
        for (let i = 0; i < traj.points.length; i++) {
          const sx = m.toScreenX(traj.points[i].x);
          const sy = m.toScreenY(traj.points[i].y);
          if (i === 0) g.moveTo(sx, sy);
          else g.lineTo(sx, sy);
        }
        g.strokePath();
      });
    }
  }

  /**
   * Animates a trajectory being drawn point by point.
   */
  private animateTrajectory(
    trajectory: TrajectoryPoint[],
    teamIndex: number,
    turnResult: TurnResult,
    expr?: string
  ): void {
    playShootSound();

    if (expr && this.mode.chatEquations && turnResult.shooter) {
      this.showChatBubble(turnResult.shooter.id, `f(x) = ${expr}`);
    }
    
    this.isAnimating = true;
    this.currentTrajectory = trajectory;
    this.animationIndex = 0;
    this.animationTimer = 0;

    const color = this.TRAJECTORY_COLORS[teamIndex];
    const totalPoints = trajectory.length;
    const drawSpeed = Math.max(1, Math.floor(totalPoints / 120));

    const animate = () => {
      if (this.animationIndex >= totalPoints) {
        this.isAnimating = false;
        this.handlePostTurn(turnResult, expr);
        return;
      }

      // Redraw past trajectories as background
      this.redrawPastTrajectories();

      const g = this.trajectoryGraphics;
      const m = this.mapper;

      // Draw current active line over past trajectories
      g.lineStyle(2, color, 0.95);
      g.beginPath();
      for (let i = 0; i <= this.animationIndex && i < totalPoints; i++) {
        const pt = trajectory[i];
        const sx = m.toScreenX(pt.x);
        const sy = m.toScreenY(pt.y);
        if (i === 0) g.moveTo(sx, sy);
        else g.lineTo(sx, sy);
      }
      g.strokePath();

      // Leading dot
      const currentPt = trajectory[Math.min(this.animationIndex, totalPoints - 1)];
      if (currentPt) {
        g.fillStyle(color, 1.0);
        g.fillCircle(m.toScreenX(currentPt.x), m.toScreenY(currentPt.y), 3.5);
      }

      this.animationIndex += drawSpeed;

      if (this.animationIndex < totalPoints) {
        this.time.delayedCall(12, animate);
      } else {
        this.time.delayedCall(200, () => {
          this.isAnimating = false;
          this.handlePostTurn(turnResult, expr);
        });
      }
    };

    animate();
  }

  /**
   * Handles post-turn logic: explosions, score updates, history log, trajectory caching, victory check, bot turn.
   */
  private handlePostTurn(turnResult: TurnResult, expr?: string): void {
    // Save trajectory to past history (capped at max 5 per team)
    if (turnResult.trajectory && turnResult.trajectory.length > 0) {
      const shooterTeam = turnResult.shooter.teamIndex;
      this.pastTrajectories.push({ teamIndex: shooterTeam, points: turnResult.trajectory });

      const t0 = this.pastTrajectories.filter(t => t.teamIndex === 0).slice(-5);
      const t1 = this.pastTrajectories.filter(t => t.teamIndex === 1).slice(-5);
      this.pastTrajectories = [...t0, ...t1];
    }

    // Redraw trajectories cleanly
    this.redrawPastTrajectories();

    // Redraw obstacles (to show carved bite holes)
    this.drawObstacles();

    // Show impact explosion if trajectory hit an obstacle or ended
    if (turnResult.trajectory && turnResult.trajectory.length > 0) {
      const lastPt = turnResult.trajectory[turnResult.trajectory.length - 1];
      this.showExplosion(lastPt.x, lastPt.y);
    }

    // Record turn in HUD history panel & replay logger
    let didPlayerMiss = false;
    let didBotKill = false;

    if (this.lastTurnExpression && this.lastShooter) {
      if (turnResult.kills.length === 0 && !this.lastShooter.isBot) {
        didPlayerMiss = true;
      }
      if (turnResult.kills.length > 0 && this.lastShooter.isBot) {
        didBotKill = true;
      }
      if (turnResult.kills.length > 0 && this.lastShooter.teamIndex !== 0) {
        this.lastKillingExpression = this.lastTurnExpression;
      }

      const shooterTeam = this.matchState.teams[turnResult.shooter.teamIndex];
      const soldierIdx = shooterTeam
        ? shooterTeam.soldiers.findIndex(s => s.id === turnResult.shooter.id)
        : 0;

      this.replayTurns.push({
        teamIndex: turnResult.shooter.teamIndex,
        soldierIndex: Math.max(0, soldierIdx),
        expression: this.lastTurnExpression,
        hits: turnResult.kills.length,
      });
      const key = `${turnResult.shooter.teamIndex}-${soldierIdx}`;
      let nameToDisplay = this.soldierNames.get(key) || (this.lastShooter.isBot ? 'BOT' : 'YOU');
      if (nameToDisplay.includes('BOT')) {
        nameToDisplay = nameToDisplay.replace(' [BOT]', '');
      }

      this.hudUI.addHistoryEntry({
        teamIndex: this.lastShooter.teamIndex,
        soldierLabel: nameToDisplay,
        expression: this.lastTurnExpression,
        hits: turnResult.kills.length,
      });
    } // Closes the if (expr) from line 733

    // Show explosions for kills
    for (const killed of turnResult.kills) {
      this.showExplosion(killed.x, killed.y);
    }
    if (turnResult.kills.length > 0) {
      setTimeout(() => playDeathSound(), 300);
    }

    // Update soldiers display
    this.drawSoldiers();

    if (this.lastTurnExpression !== null) {
      this.lastTurnExpression = null;
      this.lastShooter = null;
    }

    // Trigger Bot Trash Talk
    if (this.lastShooter && !turnResult.matchOver) {
      let botTalks = false;
      let botMsg = '';
      if (didPlayerMiss) {
        botTalks = Math.random() > 0.5; // 50% chance to trash talk on miss
        const lines = [
          "Have you considered asking AI to write your equations?",
          "That was so bad you should definitely start using AI.",
          "Even my random generator aims better.",
          "Syntax Error: Skill not found.",
        ];
        botMsg = lines[Math.floor(Math.random() * lines.length)];
      } else if (didBotKill) {
        botTalks = Math.random() > 0.3; // 70% chance to gloat on kill
        const lines = [
          "Calculated.",
          "Even a bot could dodge that... wait.",
          "f(x) = RIP",
          "My parameters are optimal. Yours are fatal.",
        ];
        botMsg = lines[Math.floor(Math.random() * lines.length)];
      }
      
      if (botTalks && botMsg) {
        this.time.delayedCall(800, () => {
          // Find the active bot's name
          const sIdx = this.matchState.teams[turnResult.shooter.teamIndex].soldiers.findIndex(s => s.id === turnResult.shooter.id);
          const name = this.soldierNames.get(`${turnResult.shooter.teamIndex}-${sIdx}`) || 'BOT';
          this.hudUI.addChatEntry(name, botMsg, false);
        });
      }
    }

    // Update scoreboard
    const alive = getAliveCount(this.matchState);
    this.hudUI.updateScore(alive[0], alive[1]);

    // Check for One Shot bot difficulty rules
    if (this.difficulty === BotDifficulty.OneShot && turnResult.shooter.teamIndex === 0 && !turnResult.matchOver) {
      if (turnResult.kills.length === 0) {
        // Player missed! Trigger immediate computer retaliation strike
        this.time.delayedCall(400, () => this.executeOneShotRetaliation());
        return;
      }
    }

    // Check for game over
    if (turnResult.matchOver) {
      const playerWon = turnResult.winnerIndex === 0;
      const winnerName = this.matchState.teams[turnResult.winnerIndex].name;
      
      if (playerWon) {
        setTimeout(() => playVictorySound(), 800);
      } else {
        setTimeout(() => playDefeatSound(), 800);
      }

      // Auto save replay to localStorage
      const replayData: ReplayData = {
        version: 1,
        createdAt: Date.now(),
        difficulty: this.difficulty,
        team1Count: this.mode.team1Count,
        team2Count: this.mode.team2Count,
        keepTraces: this.mode.keepTraces,
        obstacleDensity: this.mode.obstacleDensity,
        showCoordinates: this.mode.showCoordinates,
        turnTimer: this.mode.turnTimer,
        initialSoldiers: this.initialSoldiers,
        initialObstacles: this.matchState.obstacles,
        turns: this.replayTurns,
        chatLog: this.replayChatLog
      };
      saveLocalReplay(replayData);

      this.time.delayedCall(800, () => {
        this.hudUI.showResult(playerWon, winnerName, this.lastKillingExpression);
      });
      return;
    }

    // Check if next turn is a bot
    const nextSoldier = getCurrentSoldier(this.matchState);
    if (nextSoldier.isBot) {
      this.time.delayedCall(500, () => this.executeBotTurn());
    } else {
      this.updateHudState();
      this.hudUI.setInputEnabled(true);
    }
  }

  /**
   * Executes an instant lethal retaliation turn for One Shot mode when player misses.
   */
  private executeOneShotRetaliation(): void {
    this.hudUI.showBotThinking(true);
    this.hudUI.setTurnIndicator('Missed! Computer retaliates...', false);

    this.time.delayedCall(1000, () => {
      const botSoldier = getCurrentSoldier(this.matchState);
      const playerSoldier = this.matchState.teams[0].soldiers.find(s => s.alive);
      if (!playerSoldier) return;

      const dx = (playerSoldier.x - botSoldier.x) * -1;
      const dy = playerSoldier.y - botSoldier.y;
      const slope = (dy / dx).toFixed(4);
      const funcStr = `${slope}*x`;

      this.lastTurnExpression = funcStr;
      this.lastShooter = { teamIndex: botSoldier.teamIndex, isBot: true };

      const parseResult = parseFunction(funcStr);
      if (parseResult.ok) {
        const turnResult = applyTurn(this.matchState, parseResult.evaluate);
        this.hudUI.showBotThinking(false);
        this.animateTrajectory(turnResult.trajectory, botSoldier.teamIndex, turnResult, funcStr);
      }
    });
  }

  /**
   * Executes a bot's turn.
   */
  private executeBotTurn(): void {
    const botSoldier = getCurrentSoldier(this.matchState);

    this.hudUI.showBotThinking(true);
    this.hudUI.setTurnIndicator('Bot thinking...', false);

    // Find the nearest enemy
    const enemies = this.matchState.teams
      .filter((_, i) => i !== botSoldier.teamIndex)
      .flatMap(t => t.soldiers.filter(s => s.alive));

    let targetX = 0, targetY = 0;
    if (enemies.length > 0) {
      // Pick the nearest enemy
      let minDist = Infinity;
      for (const enemy of enemies) {
        const dist = Math.hypot(enemy.x - botSoldier.x, enemy.y - botSoldier.y);
        if (dist < minDist) {
          minDist = dist;
          targetX = enemy.x;
          targetY = enemy.y;
        }
      }
    }

    const direction: 1 | -1 = targetX >= botSoldier.x ? 1 : -1;

    // Simulate "thinking" delay
    this.time.delayedCall(800 + Math.random() * 600, () => {
      const funcStr = generateBotFunction(
        this.difficulty,
        botSoldier.x,
        botSoldier.y,
        targetX,
        targetY,
        direction,
        this.matchState.obstacles || [],
        this.dynamicBounds,
      );

      this.lastTurnExpression = funcStr;
      this.lastShooter = { teamIndex: botSoldier.teamIndex, isBot: true };

      // Parse the bot's function
      const parseResult = parseFunction(funcStr);
      if (!parseResult.ok) {
        // Fallback: simple linear function
        const fallback = parseFunction('x');
        if (!fallback.ok) return; // Should never happen
        const turnResult = applyTurn(this.matchState, fallback.evaluate);
        this.hudUI.showBotThinking(false);
        this.animateTrajectory(turnResult.trajectory, botSoldier.teamIndex, turnResult);
        return;
      }

      const turnResult = applyTurn(this.matchState, parseResult.evaluate);
      this.hudUI.showBotThinking(false);
      this.animateTrajectory(turnResult.trajectory, botSoldier.teamIndex, turnResult, parseResult.expression);
    });
  }

  /**
   * Shows a multi-layered explosion effect with flash, shockwaves, and particle sparks.
   */
  private showExplosion(worldX: number, worldY: number): void {
    const sx = this.mapper.toScreenX(worldX);
    const sy = this.mapper.toScreenY(worldY);
    const g = this.explosionGraphics;

    // Subtle camera impact shake
    this.cameras.main.shake(160, 0.0035);
    
    // Play sound
    playExplosionSound();

    // Particle sparks burst
    const particlesCount = 16;
    const particles = Array.from({ length: particlesCount }, () => {
      const angle = Math.random() * Math.PI * 2;
      const speed = 1.5 + Math.random() * 3.5;
      return {
        x: sx,
        y: sy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: 1.5 + Math.random() * 2,
        color: Math.random() > 0.3 ? 0xd94040 : 0xff9100,
      };
    });

    let frame = 0;
    const maxFrames = 25;

    const explode = () => {
      if (frame >= maxFrames) {
        g.clear();
        return;
      }

      g.clear();
      const progress = frame / maxFrames;
      const alpha = 1 - progress;

      // 1. Central flash circle
      const flashRadius = (1 - Math.pow(1 - progress, 2)) * 28;
      g.fillStyle(0xffffff, alpha * 0.7);
      g.fillCircle(sx, sy, flashRadius * 0.4);

      g.fillStyle(0xff9100, alpha * 0.3);
      g.fillCircle(sx, sy, flashRadius * 0.7);

      // 2. Shockwave expanding rings
      g.lineStyle(2, 0xd94040, alpha * 0.8);
      g.strokeCircle(sx, sy, flashRadius);

      g.lineStyle(1, 0xffffff, alpha * 0.6);
      g.strokeCircle(sx, sy, flashRadius * 0.65);

      // 3. Particle sparks update & render
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.08; // gravity force
        p.vx *= 0.96;
        p.vy *= 0.96;

        g.fillStyle(p.color, alpha);
        g.fillCircle(p.x, p.y, p.radius * (1 - progress * 0.5));
      }

      frame++;
      this.time.delayedCall(20, explode);
    };

    explode();
  }

  /**
   * Starts the turn timer if enabled.
   */
  private startTurnTimer(): void {
    this.stopTurnTimer();
    if (this.mode.turnTimer <= 0) return;

    this.secondsRemaining = this.mode.turnTimer;
    this.hudUI.updateTimer(this.secondsRemaining);

    this.turnTimerEvent = this.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        this.secondsRemaining--;
        this.hudUI.updateTimer(this.secondsRemaining);
        
        if (this.secondsRemaining <= 5 && this.secondsRemaining > 0) {
          playTickSound();
        }

        if (this.secondsRemaining <= 0) {
          this.stopTurnTimer();
          this.hudUI.forceFire();
        }
      }
    });
  }

  /**
   * Stops the turn timer.
   */
  private stopTurnTimer(): void {
    if (this.turnTimerEvent) {
      this.turnTimerEvent.remove();
      this.turnTimerEvent = null;
    }
    this.hudUI.hideTimer();
  }

  /**
   * Updates the HUD to reflect current match state.
   */
  private updateHudState(): void {
    const alive = getAliveCount(this.matchState);
    this.hudUI.updateScore(alive[0], alive[1]);

    const currentSoldier = getCurrentSoldier(this.matchState);
    if (currentSoldier.isBot) {
      this.hudUI.setTurnIndicator('Bot turn', false);
      this.hudUI.setInputEnabled(false);
      this.stopTurnTimer();
    } else {
      this.hudUI.setTurnIndicator('Your turn', true);
      this.hudUI.setInputEnabled(true);
      this.startTurnTimer();
    }
  }

  /**
   * Restarts the game with a new match.
   */
  private restartGame(): void {
    this.hudUI.destroy();
    this.scene.restart({ difficulty: this.difficulty, mode: this.mode });
  }

  /**
   * Returns to the main menu.
   */
  private goToMenu(): void {
    this.hudUI.destroy();
    this.scene.start('MenuScene');
  }


  shutdown(): void {
    this.hudUI.destroy();
  }
}
